import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import RmbsTrancheDrawer from './RmbsTrancheDrawer';
import RmbsTapeImportModal from './RmbsTapeImportModal';

const Wrapper = styled.div`padding: 22px; color: #e2e8f0;`;
const Header = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 18px;`;
const Title = styled.h2`margin: 0; display: flex; align-items: center; gap: 8px;`;
const Sub = styled.div`color: #94a3b8; margin-top: 4px; font-size: 13px;`;

const Button = styled.button<{ $danger?: boolean }>`
  border: 1px solid ${p => p.$danger ? 'rgba(248,113,113,.45)' : 'rgba(56,189,248,.4)'};
  background: ${p => p.$danger ? 'rgba(127,29,29,.25)' : 'rgba(14,116,144,.22)'};
  color: ${p => p.$danger ? '#fca5a5' : '#bae6fd'};
  border-radius: 8px; padding: 8px 11px; font-size: 12px; font-weight: 700; cursor: pointer;
`;

const Kpis = styled.div`display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 20px;`;
const Kpi = styled.div<{ $accent?: string }>`
  background: rgba(15, 23, 42, 0.55); border: 1px solid rgba(148,163,184,.14);
  border-left: 3px solid ${p => p.$accent || '#38bdf8'}; border-radius: 10px; padding: 14px 16px;
  .label { color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  .value { font-size: 22px; font-weight: 700; color: #f8fafc; margin-top: 6px; }
`;

const SectionTitle = styled.h3`font-size: 14px; color: #cbd5e1; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: .05em;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; background: rgba(15, 23, 42, 0.45); border: 1px solid rgba(148,163,184,.12);
  th, td { text-align: left; padding: 11px 12px; border-bottom: 1px solid rgba(148,163,184,.1); font-size: 13px; }
  th { color: #94a3b8; font-size: 11px; text-transform: uppercase; }
  tbody tr.clickable { cursor: pointer; }
  tbody tr.clickable:hover { background: rgba(30, 41, 59, 0.65); }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

const Badge = styled.span<{ $tone?: string }>`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: ${p => p.$tone === 'agency' ? 'rgba(34,197,94,.16)' : 'rgba(139,92,246,.16)'};
  color: ${p => p.$tone === 'agency' ? '#86efac' : '#c4b5fd'};
`;

const ModalBackdrop = styled.div`position: fixed; inset: 0; background: rgba(2,6,23,.72); z-index: 1300; display: grid; place-items: center;`;
const Modal = styled.div`width: min(760px, 94vw); max-height: 86vh; overflow: auto; background: #0f172a; border: 1px solid rgba(148,163,184,.22); border-radius: 12px; padding: 22px;`;

const money = (v: any) => v == null ? '--' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(Number(v));
const pct = (v: any) => v == null ? '--' : `${Number(v).toFixed(2)}%`;

const RMBSManagementScreen: React.FC<{ embedded?: boolean }> = () => {
  const navigate = useNavigate();
  const [pools, setPools] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [surv, setSurv] = useState<any | null>(null);
  const [survPool, setSurvPool] = useState<any | null>(null);
  const [tapePool, setTapePool] = useState<any | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [poolRes, posRes] = await Promise.all([
        axios.get('/api/credit/rmbs/pools'),
        axios.get('/api/positions/latest'),
      ]);
      setPools(Array.isArray(poolRes.data) ? poolRes.data : []);
      const all = posRes.data?.content || posRes.data || [];
      setPositions((Array.isArray(all) ? all : []).filter((p: any) => p.assetClassName === 'RESIDENTIAL_MBS' && Number(p.quantity) !== 0));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load RMBS data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSurveillance = async (pool: any) => {
    setSurvPool(pool); setSurv(null);
    try {
      const res = await axios.get(`/api/credit/rmbs/pools/${pool.poolId}/surveillance`);
      setSurv(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load surveillance');
    }
  };

  const totalBalance = pools.reduce((s, p) => s + Number(p.currentPoolBalance || 0), 0);
  const trancheCount = pools.reduce((s, p) => s + Number(p.trancheCount || 0), 0);
  const wtd = (key: string) => {
    const num = pools.reduce((s, p) => s + Number(p[key] || 0) * Number(p.currentPoolBalance || 0), 0);
    return totalBalance > 0 ? num / totalBalance : 0;
  };

  return (
    <Wrapper>
      <Header>
        <div>
          <Title>🏠 RMBS — Pools &amp; Tranches</Title>
          <Sub>Residential MBS book of record — agency pass-through + non-agency PLS. Pool stratification &amp; delinquency surveillance.</Sub>
        </div>
        <Button onClick={() => navigate('/trade-entry/rmbs-tranche')}>New RMBS Tranche</Button>
      </Header>

      {error && <div style={{ color: '#fca5a5', marginBottom: 12 }}>{error}</div>}

      <Kpis>
        <Kpi $accent="#38bdf8"><div className="label">Total Pools</div><div className="value">{pools.length}</div></Kpi>
        <Kpi $accent="#34d399"><div className="label">Tranches</div><div className="value">{trancheCount}</div></Kpi>
        <Kpi $accent="#a78bfa"><div className="label">Pool Balance</div><div className="value">{money(totalBalance)}</div></Kpi>
        <Kpi $accent="#fbbf24"><div className="label">Wtd 3M CPR</div><div className="value">{pct(wtd('cpr3mPct'))}</div></Kpi>
        <Kpi $accent="#f87171"><div className="label">Wtd 90+ DQ</div><div className="value">{pct(wtd('delinquency90PlusPct'))}</div></Kpi>
        <Kpi $accent="#22d3ee"><div className="label">Positions</div><div className="value">{positions.length}</div></Kpi>
      </Kpis>

      {loading ? <div>Loading RMBS…</div> : (
        <>
          <SectionTitle>Pools</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Deal</th><th>Agency</th><th>Type</th><th className="num">Balance</th>
                <th className="num"># Loans</th><th className="num">3M CPR</th><th className="num">CDR</th>
                <th className="num">90+ DQ</th><th>Tranches</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pools.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: '#64748b' }}>No RMBS deals yet.</td></tr>}
              {pools.map((p) => (
                <tr key={p.poolId}>
                  <td>{p.dealName}</td>
                  <td><Badge $tone={p.agency && p.agency !== 'NON_AGENCY' ? 'agency' : 'pls'}>{p.agency}</Badge></td>
                  <td>{p.dealType}</td>
                  <td className="num">{money(p.currentPoolBalance)}</td>
                  <td className="num">{p.numLoansRemaining ?? '--'}</td>
                  <td className="num">{pct(p.cpr3mPct)}</td>
                  <td className="num">{pct(p.cdrPct)}</td>
                  <td className="num" style={{ color: Number(p.delinquency90PlusPct) > 2 ? '#f87171' : undefined }}>{pct(p.delinquency90PlusPct)}</td>
                  <td className="num">{p.trancheCount}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Button onClick={() => openSurveillance(p)}>Surveillance</Button>{' '}
                    <Button onClick={() => setTapePool(p)}>Import Tape</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <SectionTitle>Positions</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Security</th><th>CUSIP</th><th>Portfolio</th>
                <th className="num">Face</th><th className="num">Market Value</th><th className="num">Unrealized P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b' }}>No RMBS positions.</td></tr>}
              {positions.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => setSelectedPositionId(p.id)}>
                  <td>{p.securityName}</td>
                  <td>{p.cusip}</td>
                  <td>{p.portfolioName}</td>
                  <td className="num">{money(p.quantity)}</td>
                  <td className="num">{money(p.marketValue)}</td>
                  <td className="num" style={{ color: Number(p.unrealizedPnl) < 0 ? '#f87171' : '#34d399' }}>{money(p.unrealizedPnl)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {selectedPositionId != null && (
        <RmbsTrancheDrawer positionId={selectedPositionId} onClose={() => setSelectedPositionId(null)} />
      )}

      {tapePool && (
        <RmbsTapeImportModal
          poolId={tapePool.poolId}
          dealName={tapePool.dealName}
          onClose={() => setTapePool(null)}
          onLoaded={load}
        />
      )}

      {survPool && (
        <ModalBackdrop onMouseDown={() => { setSurvPool(null); setSurv(null); }}>
          <Modal onMouseDown={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Surveillance — {survPool.dealName}</h3>
            {!surv ? <div>Loading…</div> : (
              <>
                <SectionTitle>Delinquency Pipeline</SectionTitle>
                {surv.latestDelinquency ? (
                  <Table>
                    <thead><tr><th className="num">Current</th><th className="num">30</th><th className="num">60</th><th className="num">90+</th><th className="num">FC</th><th className="num">REO</th><th className="num">CPR</th><th className="num">CDR</th><th className="num">Sev</th></tr></thead>
                    <tbody><tr>
                      <td className="num">{pct(surv.latestDelinquency.current_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.dq_30_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.dq_60_pct)}</td>
                      <td className="num" style={{ color: '#f87171' }}>{pct(surv.latestDelinquency.dq_90_plus_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.foreclosure_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.reo_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.cpr_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.cdr_pct)}</td>
                      <td className="num">{pct(surv.latestDelinquency.severity_pct)}</td>
                    </tr></tbody>
                  </Table>
                ) : <div style={{ color: '#64748b' }}>No delinquency data recorded.</div>}

                {surv.stratification && Object.keys(surv.stratification).length > 0 && (
                  <>
                    <SectionTitle>Stratification</SectionTitle>
                    {Object.entries(surv.stratification).map(([dim, bands]: any) => (
                      <div key={dim} style={{ marginBottom: 14 }}>
                        <div style={{ color: '#cbd5e1', fontWeight: 600, marginBottom: 6 }}>{dim}</div>
                        <Table>
                          <thead><tr><th>Band</th><th className="num">Balance</th><th className="num">%</th><th className="num"># Loans</th><th className="num">WA Coupon</th><th className="num">WA FICO</th><th className="num">DQ</th></tr></thead>
                          <tbody>
                            {(bands as any[]).map((b: any, i: number) => (
                              <tr key={i}>
                                <td>{b.band}</td>
                                <td className="num">{money(b.balance)}</td>
                                <td className="num">{pct(b.balance_pct)}</td>
                                <td className="num">{b.num_loans ?? '--'}</td>
                                <td className="num">{pct(b.wa_coupon_pct)}</td>
                                <td className="num">{b.wa_fico ?? '--'}</td>
                                <td className="num">{pct(b.delinquency_pct)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ))}
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <Button onClick={() => { setSurvPool(null); setSurv(null); }}>Close</Button>
                </div>
              </>
            )}
          </Modal>
        </ModalBackdrop>
      )}
    </Wrapper>
  );
};

export default RMBSManagementScreen;
